import { useEffect, useRef, type ReactNode } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, {
  type DateClickArg,
} from '@fullcalendar/interaction';
import type {
  DateRangeInput,
  DayCellContentArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';

interface PlannerCalendarProps {
  events?: EventInput[];
  dateClick?: (arg: DateClickArg) => void;
  eventClick?: (arg: EventClickArg) => void;
  dayCellContent?: (arg: DayCellContentArg) => ReactNode;
  validRange?: DateRangeInput | ((now: Date) => DateRangeInput);
  mobileBreakpoint?: number;
  locale?: string;
}

export default function PlannerCalendar({
  events = [],
  dateClick,
  eventClick,
  dayCellContent,
  validRange,
  mobileBreakpoint = 768,
  locale = 'de',
}: PlannerCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    const updateView = () => {
      const api = calendarRef.current?.getApi();
      if (!api) return;

      api.changeView(
        window.innerWidth <= mobileBreakpoint
          ? 'dayGridWeek'
          : 'dayGridMonth'
      );
    };

    updateView();
    window.addEventListener('resize', updateView);
    return () => window.removeEventListener('resize', updateView);
  }, [mobileBreakpoint]);

  return (
    <FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      headerToolbar={{
        left: 'title',
        center: '',
        right: 'prev,next',
      }}
      timeZone="Europe/Berlin"
      locales={[deLocale]}
      locale={locale}
      aspectRatio={2}
      expandRows={false}
      events={events}
      {...(dateClick ? { dateClick } : {})}
      {...(eventClick ? { eventClick } : {})}
      {...(dayCellContent ? { dayCellContent } : {})}
      {...(validRange ? { validRange } : {})}
    />
  );
}
